import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { fetchCampaignMetadata, fetchCampaignSearchFilters } from '~/services/campaign';

interface CscOption {
  id: number;
  full_name: string;
}

interface StatusOption {
  code: string;
  name: string;
}

// "No activity since <date>" is the honest way to describe the filter, but it
// makes the reader do the arithmetic: to see campaigns quiet for a year you have
// to work out what today minus a year was. These do it for you (HHLTB-2125).
const QUIET_FOR_PRESETS: { label: string; months: number }[] = [
  { label: 'Quiet 3+ months', months: 3 },
  { label: 'Quiet 6+ months', months: 6 },
  { label: 'Quiet 1+ year', months: 12 },
  { label: 'Quiet 2+ years', months: 24 },
];

const monthsAgo = (months: number): string => {
  const date = new Date();
  date.setMonth(date.getMonth() - months);
  return date.toISOString().split('T')[0];
};

const CampaignLastActivityAccordionItem: React.FC = () => {
  const navigate = useNavigate();
  const [inactiveSince, setInactiveSince] = useState('');
  const [campaignStatus, setCampaignStatus] = useState('');
  const [csc, setCsc] = useState('');
  const [cscOptions, setCscOptions] = useState<CscOption[]>([]);
  const [statusOptions, setStatusOptions] = useState<StatusOption[]>([]);

  useEffect(() => {
    fetchCampaignMetadata()
      .then((metadata) => {
        if (metadata?.patient_services_coordinators) {
          setCscOptions(metadata.patient_services_coordinators);
        }
      })
      .catch((error) => console.error('Error fetching CSCs:', error));

    fetchCampaignSearchFilters()
      .then((filters) => {
        if (filters?.campaign_statuses) setStatusOptions(filters.campaign_statuses);
      })
      .catch((error) => console.error('Error fetching campaign statuses:', error));
  }, []);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const params = new URLSearchParams();
    if (inactiveSince) params.set('inactive_since', inactiveSince);
    if (campaignStatus) params.set('campaign_status', campaignStatus);
    if (csc) params.set('csc', csc);
    navigate(`/reports/predefined/campaign-last-activity/results?${params.toString()}`);
  };

  return (
    <div className='flex h-full w-full flex-col rounded-sm border border-stroke bg-white px-7.5 py-5 shadow-default'>
      <h4 className='text-title-xsm font-bold sm:text-title-md'>Campaign Last Activity</h4>
      <p className='mt-2 text-sm text-body'>
        The most recent donation, update, guestbook entry, message, or fund request on
        each campaign, stalest first. Covers open campaigns unless you pick a status. Set
        a date to list only the campaigns with nothing since then, including campaigns
        that have never had any activity at all.
      </p>
      <form onSubmit={handleSubmit} className='mt-4 flex flex-1 flex-col'>
        <div className='flex flex-col gap-4'>
          <div>
            <label className='block text-[12px] font-bold mb-1'>No activity since</label>
            <input
              type='date'
              value={inactiveSince}
              onChange={(e) => setInactiveSince(e.target.value)}
              className='h-10 w-full rounded border border-gray-400 px-3 text-sm'
            />
            <div className='mt-2 flex flex-wrap gap-2'>
              {QUIET_FOR_PRESETS.map((preset) => (
                <button
                  key={preset.months}
                  type='button'
                  onClick={() => setInactiveSince(monthsAgo(preset.months))}
                  className='rounded border border-gray-400 px-2 py-1 text-[11px] hover:bg-gray-100'
                >
                  {preset.label}
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className='block text-[12px] font-bold mb-1'>Status</label>
            <select
              value={campaignStatus}
              onChange={(e) => setCampaignStatus(e.target.value)}
              className='h-10 w-full rounded border border-gray-400 px-3 text-sm'
            >
              <option value=''>Open campaigns</option>
              {statusOptions.map((option) => (
                <option key={option.code} value={option.code}>
                  {option.name}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className='block text-[12px] font-bold mb-1'>CSC</label>
            <select
              value={csc}
              onChange={(e) => setCsc(e.target.value)}
              className='h-10 w-full rounded border border-gray-400 px-3 text-sm'
            >
              <option value=''>All CSCs</option>
              {cscOptions.map((option) => (
                <option key={option.id} value={String(option.id)}>
                  {option.full_name}
                </option>
              ))}
            </select>
          </div>
        </div>
        <div className='mt-auto pt-6'>
          <button type='submit' className='rounded-lg bg-primary px-8 py-3 text-sm font-medium text-white'>
            Run report
          </button>
        </div>
      </form>
    </div>
  );
};

export default CampaignLastActivityAccordionItem;
